import { BrokerInvoiceStatus } from '@numeo/types-core'
import type { TmsInvoiceRow } from '@numeo/types-main-web-app'
import { Badge } from '@/components/ds2'
import { STATUS_GROUP_LABEL } from './format'

// How the Invoices page shows a Ready-tab load that cannot be invoiced now (`ar_status`); any other status reads "Not ready".
const NOT_READY_LABEL: Partial<Record<NonNullable<TmsInvoiceRow['ar_status']>, string>> = { draft: 'Draft', issue: 'Issue' }

// What an invoice in the Issues group is stuck on, by its stored status; "Issues" alone says nothing a user can act on.
const ISSUE_LABEL: Partial<Record<BrokerInvoiceStatus, string>> = {
    [BrokerInvoiceStatus.HELD]: 'Held by factor',
    [BrokerInvoiceStatus.DENIED]: 'Denied',
    [BrokerInvoiceStatus.DISPUTED]: 'Disputed',
}

/** An Issues invoice's status: what it is stuck on, and the factor's hold reason under it when stored. */
const IssueStatus = ({ row }: { row: TmsInvoiceRow }) => {
    const label = (row.status_stored && ISSUE_LABEL[row.status_stored]) ?? 'Needs attention'
    return (
        <span className="flex flex-col gap-0.5">
            <Badge variant="soft" type={row.status_stored === BrokerInvoiceStatus.HELD ? 'warning' : 'danger'} size="sm" label={label} />
            {row.hold_reason && (
                <span className="max-w-[16rem] truncate text-xs text-foreground-muted" title={row.hold_reason}>
                    {row.hold_reason}
                </span>
            )}
        </span>
    )
}

/** A row from the Ready tab, which the chat lists one load per item; any other row is an invoice. */
export const isReadyTabRow = (row: TmsInvoiceRow): boolean => row.status_group === 'ready_to_invoice' || row.readiness !== undefined

/**
 * A row's status: a Ready-tab row says whether it can be invoiced now, by its readiness, else its page status; an invoice row its
 * Invoices page group. A Ready-tab row is never labelled by its tab ("Ready to invoice"): the tab holds loads that are not ready too.
 */
export const StatusBadge = ({ row }: { row: TmsInvoiceRow }) => {
    const ready = row.readiness ? row.readiness === 'ready' : row.ar_status === 'ready'
    if (row.status_group === 'issues' && !row.readiness) return <IssueStatus row={row} />
    if (row.status_group !== 'ready_to_invoice' && !row.readiness)
        return <Badge variant="soft" type="default" size="md" label={STATUS_GROUP_LABEL[row.status_group] ?? row.status_group} />
    if (ready) return <Badge variant="soft" type="success" size="sm" label="Ready" />
    if (row.readiness === 'not_ready' || row.ar_status) {
        return <Badge variant="soft" type="warning" size="sm" label={(row.ar_status && NOT_READY_LABEL[row.ar_status]) ?? 'Not ready'} />
    }
    return <Badge variant="soft" type="default" size="md" label="Not invoiced" />
}
